import { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Layer manager panel — shows all canvas objects with reorder/visibility/delete controls.
 */
export const LayerManager = ({ fabricRef, isDark, onUpdate }) => {
  const [layers, setLayers] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const refreshLayers = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const objects = canvas.getObjects().map((obj, index) => ({
      id: obj.__id || `layer-${index}`,
      name: obj.__layerName || (obj.type === 'textbox' ? 'Text' : obj.type === 'image' ? 'Image' : `Object ${index + 1}`),
      type: obj.type,
      visible: obj.visible !== false,
      index,
      obj,
    }));

    setLayers(objects.reverse()); // Top layer first
    const active = canvas.getActiveObject();
    setActiveId(active?.__id || null);
  }, [fabricRef]);

  useEffect(() => {
    refreshLayers();
  }, [refreshLayers, onUpdate]);

  // Wire canvas events for live updates
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const handler = () => refreshLayers();
    canvas.on('object:added', handler);
    canvas.on('object:removed', handler);
    canvas.on('selection:created', handler);
    canvas.on('selection:updated', handler);
    canvas.on('selection:cleared', handler);

    return () => {
      canvas.off('object:added', handler);
      canvas.off('object:removed', handler);
      canvas.off('selection:created', handler);
      canvas.off('selection:updated', handler);
      canvas.off('selection:cleared', handler);
    };
  }, [fabricRef, refreshLayers]);

  const selectLayer = (layer) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.setActiveObject(layer.obj);
    canvas.renderAll();
    setActiveId(layer.id);
  };

  const toggleVisibility = (layer) => {
    layer.obj.visible = !layer.obj.visible;
    fabricRef.current?.renderAll();
    refreshLayers();
  };

  const deleteLayer = (layer) => {
    if (layer.obj.__undeletable) return;
    fabricRef.current?.remove(layer.obj);
    fabricRef.current?.renderAll();
    refreshLayers();
  };

  const moveUp = (layer) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.bringObjectForward(layer.obj);
    canvas.renderAll();
    refreshLayers();
  };

  const moveDown = (layer) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.sendObjectBackwards(layer.obj);
    canvas.renderAll();
    refreshLayers();
  };

  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const hoverClass = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50';
  const activeClass = isDark ? 'bg-white/10' : 'bg-gray-100';

  if (layers.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className={cn("text-sm", mutedClass)}>No layers yet</p>
        <p className={cn("text-xs mt-1", mutedClass)}>Add text or stickers to get started</p>
      </div>
    );
  }

  return (
    <div className="max-h-60 overflow-y-auto">
      {layers.map((layer) => (
        <div
          key={layer.id}
          className={cn(
            "flex items-center gap-2 px-3 py-2 border-b cursor-pointer transition-colors",
            borderClass,
            hoverClass,
            activeId === layer.id && activeClass
          )}
          onClick={() => selectLayer(layer)}
        >
          <span className={cn("text-xs flex-1 truncate", textClass)}>
            {layer.name}
          </span>

          <button
            onClick={(e) => { e.stopPropagation(); moveUp(layer); }}
            className={cn("p-1", mutedClass)}
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); moveDown(layer); }}
            className={cn("p-1", mutedClass)}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleVisibility(layer); }}
            className={cn("p-1", mutedClass)}
          >
            {layer.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </button>
          {!layer.obj.__undeletable && (
            <button
              onClick={(e) => { e.stopPropagation(); deleteLayer(layer); }}
              className="p-1 text-red-500/60 hover:text-red-500"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
